import { useEffect, useState } from 'react';
import { TInsightsChangeEvent } from '../api/types/insights-change-event';

/**
 * Pins each change notice to the point in the conversation where it happened,
 * so notices read chronologically instead of clumping at the end of the thread.
 * An anchor is the message count at the moment the event was first seen, and it
 * never moves again — later messages push the conversation past it, not it past
 * the conversation.
 *
 * Returns the events anchored at a given message index, plus a reset for when
 * the thread is emptied and surviving notices have to re-anchor to the top.
 */
export const useNoticeAnchors = (events: TInsightsChangeEvent[], messageCount: number) => {
	const [anchors, setAnchors] = useState<Map<number, number>>(new Map());

	useEffect(() => {
		setAnchors((prev) => {
			const next = new Map<number, number>();
			events.forEach((ev) => {
				next.set(ev.id, prev.get(ev.id) ?? messageCount);
			});
			return next;
		});
	}, [events]);

	const noticesAnchoredAt = (index: number) =>
		events.filter((ev) => Math.min(anchors.get(ev.id) ?? messageCount, messageCount) === index);

	return { noticesAnchoredAt, resetAnchors: () => setAnchors(new Map()) };
};
